import { Facebook, ArrowUpRight } from "lucide-react";
import { motion } from "framer-motion";
import { SOCIAL, BRAND } from "@/lib/content";

function TikTokIcon({ className = "w-5 h-5" }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.21a8.27 8.27 0 0 0 4.83 1.54V6.3a4.85 4.85 0 0 1-1.9-.61Z" />
    </svg>
  );
}

/**
 * Community / social-proof strip — sits between hero and pathway selector.
 * Leverages Krzysztof's existing presence on Facebook + TikTok where many
 * applicants already recognise him from videos and the community group.
 */
export default function SocialStripe() {
  return (
    <section className="py-10 md:py-14 bg-[#EBE9E1]/40 border-y border-[#E5E3D8]" data-testid="home-social-stripe">
      <div className="container-sp">
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.5 }}
          className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 items-center"
        >
          <div className="md:col-span-5">
            <div className="overline">Recognised by the community</div>
            <h3 className="font-serif text-2xl md:text-3xl text-[#0D1B2A] mt-2 leading-tight">
              You may already know <span className="italic text-[#D7141A]">{BRAND.expertName.split(" ")[0]}</span> from Facebook & TikTok.
            </h3>
          </div>

          <div className="md:col-span-7 flex flex-col sm:flex-row gap-4">
            <a
              href={SOCIAL.facebook}
              target="_blank"
              rel="noreferrer"
              data-testid="social-stripe-facebook"
              className="group flex-1 flex items-center justify-between gap-4 bg-white border border-[#EFEEE8] hover:border-[#D7141A]/40 rounded-2xl px-5 py-4 transition-all"
            >
              <div className="flex items-center gap-3.5">
                <span className="w-11 h-11 rounded-full bg-[#1877F2] flex items-center justify-center text-white shrink-0">
                  <Facebook className="w-5 h-5" />
                </span>
                <div className="leading-tight">
                  <div className="text-xs text-[#718096] font-medium">
                    Facebook community
                  </div>
                  <div className="text-sm font-semibold text-[#0D1B2A] mt-0.5">
                    StudyPoland.Info
                  </div>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-[#0D1B2A] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 shrink-0" />
            </a>

            <a
              href={SOCIAL.tiktok}
              target="_blank"
              rel="noreferrer"
              data-testid="social-stripe-tiktok"
              className="group flex-1 flex items-center justify-between gap-4 bg-white border border-[#EFEEE8] hover:border-[#D7141A]/40 rounded-2xl px-5 py-4 transition-all"
            >
              <div className="flex items-center gap-3.5">
                <span className="w-11 h-11 rounded-full bg-[#0D1B2A] flex items-center justify-center text-white shrink-0">
                  <TikTokIcon className="w-5 h-5" />
                </span>
                <div className="leading-tight">
                  <div className="text-xs text-[#718096] font-medium">
                    TikTok · daily tips
                  </div>
                  <div className="text-sm font-semibold text-[#0D1B2A] mt-0.5">
                    @krzysztof_szymanski
                  </div>
                </div>
              </div>
              <ArrowUpRight className="w-4 h-4 text-[#0D1B2A] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 shrink-0" />
            </a>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
